/**
 * Super Admin acting-institute request helper.
 *
 * A Super Admin who selects an institute from the dropdown keeps their SA token
 * (the JWT has NO operating institute context), so the acting institute must be
 * sent explicitly on each request or the backend resolver
 * (resolveInstituteIdForUser / resolveActingInstituteContext) cannot scope the
 * query and returns "Institute context required" / "Missing or unresolvable
 * institute context".
 *
 * We thread the id as an `instituteId` QUERY param — the first source the
 * backend Super Admin fallback reads (req.query.instituteId). A query param is
 * used instead of a custom header because the backend CORS `allowedHeaders`
 * list does not include a custom institute header, so a header would be blocked
 * by the browser preflight in production; a query param needs no CORS change
 * and works for GET/POST/PUT/PATCH/DELETE alike (axios serializes `params` for
 * every method).
 *
 * Safe for non-SA roles: the backend only consults this fallback when the
 * caller is a Super Admin with no token-based operating context;
 * admin/institute/teacher/hod always resolve from their own profile/token
 * first, so the extra param is ignored for them.
 *
 * Fully guarded (No-Crash rules 2/3): a malformed localStorage value can never
 * throw during a request. Always returns an axios config object with any
 * existing `params` preserved and merged.
 *
 * @param {object} [config] - an axios request config (e.g. { params, headers }).
 * @returns {object} the config, with `params.instituteId` added when a
 *   selected institute is present and the caller didn't already set it.
 */
export const withActingInstitute = (config = {}) => {
  try {
    const raw = localStorage.getItem("selectedInstitute");
    const selected = raw ? JSON.parse(raw) : null;
    const id = selected?.id;
    if (id != null && id !== "") {
      const params = { ...(config.params || {}) };
      // Never clobber an explicit instituteId the caller already provided.
      if (params.instituteId == null) {
        params.instituteId = id;
      }
      return { ...config, params };
    }
  } catch (e) {
    // Ignore parse errors — degrade to the unmodified config (unchanged behavior).
  }
  return config;
};

export default withActingInstitute;
